import type { FC, ReactNode } from 'react';

import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { Box, Typography } from '@mui/material';

import type { Country } from 'src/api/zrm';
import { formatClockSeconds } from 'src/tv/dashboardFormat';
import CityIcon from 'src/tv/live/CityIcons';
import type { Call } from 'src/tv/live/places';
import type { Visit } from 'src/tv/live/useLiveFeed';
import { TV_COLORS } from 'src/tv/tvTheme';
import languageFlags from 'src/utils/language/LanguageFlags';

import type { TFunction } from 'i18next';

// More than fit on any screen: the list is cut at the bottom of the screen, not at a count.
export const FEED_LENGTH = 40;
// The rows fade out over the lower part of the list.
const FADE = 'linear-gradient(to bottom, #000 45%, transparent 100%)';
const ICON_SX = { fontSize: '1.4vw' };
const MUTED = { color: TV_COLORS.muted };

export interface FeedEntry {
  id: string;
  at: number;
  icon: ReactNode;
  iconTitle?: string;
  content: ReactNode;
}

/** The customer's or visitor's country. */
const Flag: FC<{ country: Country }> = (props) => {
  const { country } = props;

  return (
    <Box
      component="img"
      alt={country}
      src={languageFlags[country]}
      sx={{ height: '0.9em', flexShrink: 0 }}
    />
  );
};

const DIRECTION_ICONS = { outgoing: <CallMadeIcon sx={ICON_SX} />, incoming: <CallReceivedIcon sx={ICON_SX} /> };

/** Telavox's start time; the office icon and name, then the customer's region and country. */
export const callEntry = (call: Call, t: TFunction): FeedEntry => {
  const directionTitles = { outgoing: t('Outgoing call'), incoming: t('Incoming call') };

  return {
    id: call.id,
    at: call.startedAt,
    // zrm sends no direction when Telavox gives none; then there is no icon.
    icon: call.direction ? DIRECTION_ICONS[call.direction] : null,
    iconTitle: call.direction ? directionTitles[call.direction] : undefined,
    content: (
      <>
        <CityIcon
          office={call.office.id}
          size="1.5vw"
        />
        <Box
          component="span"
          sx={{ fontWeight: 600 }}
        >
          {call.office.name}
        </Box>
        <Box
          component="span"
          sx={MUTED}
        >
          →
        </Box>
        <Box component="span">{call.customer.name}</Box>
        <Flag country={call.country} />
      </>
    ),
  };
};

/** The visit's time; the town, the country's flag and the form's domain. */
export const visitEntry = (visit: Visit, t: TFunction): FeedEntry => ({
  id: visit.id,
  at: visit.at,
  icon: visit.kind === 'submitted' ? <TaskAltIcon sx={ICON_SX} /> : <FingerprintIcon sx={ICON_SX} />,
  iconTitle: visit.kind === 'submitted' ? t('Submitted') : t('Identified'),
  content: (
    <>
      <Box
        component="span"
        sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {visit.city ?? '—'}
      </Box>
      <Flag country={visit.country} />
      <Box
        component="span"
        sx={{ ...MUTED, ml: 'auto', fontSize: '1vw', whiteSpace: 'nowrap' }}
      >
        {visit.domain}
      </Box>
    </>
  ),
});

interface LiveFeedProps {
  title: string;
  /** Newest first. */
  entries: FeedEntry[];
  timeZone: string;
  country: Country;
}

/** The list on the right of a live screen: the latest calls and visits, fading out downwards. */
const LiveFeed: FC<LiveFeedProps> = (props) => {
  const { title, entries, timeZone, country } = props;

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1vh', minHeight: 0, overflow: 'hidden' }}>
      <Typography sx={{ ...MUTED, fontSize: '1vw', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
        {title}
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: '1vh', overflow: 'hidden', maskImage: FADE }}>
        {entries.slice(0, FEED_LENGTH).map((entry) => (
          <Box
            key={entry.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.8vw',
              fontSize: '1.25vw',
              flexShrink: 0,
              borderBottom: `1px solid ${TV_COLORS.line}`,
              pb: '0.8vh',
            }}
          >
            <Box
              component="span"
              sx={{ ...MUTED, fontVariantNumeric: 'tabular-nums' }}
            >
              {formatClockSeconds(entry.at, timeZone, country)}
            </Box>
            <Box
              component="span"
              title={entry.iconTitle}
              sx={{ display: 'flex', width: '1.4vw' }}
            >
              {entry.icon}
            </Box>
            {entry.content}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default LiveFeed;
